import { Office } from '../entities/office';
import { BaseOrganizationEndpoints } from './base';
import { IResource, ResourceMapVersion } from '../entities/resource';
import { PaginatedResult } from './response/paginated_result';

export type DivisionType = 'country' | 'region' | 'campus' | 'other';

export type Division = {
  id: string;
  name: string;
  sort_order?: number;
  type: DivisionType;
  parent_id: string | null;
  /** Map-builder JSON for the division's own map (e.g. a campus map),
   *  same format as a floor resource's map_data. */
  map_data?: string | null;
};

export type DivisionsTree = Division & {
  children: DivisionsTree[];
  offices: Office[];
  floors?: IResource[];
};

export type DivisionWithOffices = DivisionsTree;

export type DivisionsFilter = Partial<Division>;
type PaginatedDivisions = Division[]; // PaginatedResult<Division>;

const BASE_PATH = '/v1/divisions';

export type DivisionUpdatePayload = Division;
export type DivisionAddPayload = Omit<DivisionUpdatePayload, 'id'>;

export class DivisionsClient extends BaseOrganizationEndpoints {
  async create(payload: DivisionAddPayload) {
    return this.requestWithStatus<Division>('POST', BASE_PATH, payload);
  }

  async delete(id: string) {
    return this.requestWithStatus<Division>('DELETE', `${BASE_PATH}/${id}`);
  }

  async update(payload: DivisionUpdatePayload) {
    return this.requestWithStatus<Division>(
      'PUT',
      `${BASE_PATH}/${payload.id}`,
      payload
    );
  }

  async all(params?: DivisionsFilter) {
    const urlParams = this.formatGenericParams(params);
    return this.requestWithStatus<PaginatedDivisions>(
      'GET',
      `${BASE_PATH}?${urlParams.toString()}`
    );
  }

  /** List a division's map versions — mirrors `/v1/resources/:id/maps`. */
  async mapsGet(divisionId: string) {
    return this.requestWithStatus<PaginatedResult<ResourceMapVersion>>(
      'GET',
      `${BASE_PATH}/${encodeURIComponent(divisionId)}/maps`
    );
  }

  /** Save a new map version for a division — mirrors the floor save flow. */
  async mapPost(
    divisionId: string,
    body: { map_data: string; map_active_from?: string }
  ) {
    return this.requestWithStatus<ResourceMapVersion>(
      'POST',
      `${BASE_PATH}/${encodeURIComponent(divisionId)}/maps`,
      body
    );
  }

  /** Activate an existing map version for a division. */
  async mapActivate(divisionId: string, mapVersionId: string) {
    return this.requestWithStatus<ResourceMapVersion>(
      'PUT',
      `${BASE_PATH}/${encodeURIComponent(divisionId)}/maps/${encodeURIComponent(mapVersionId)}`,
      { map_active_from: new Date().toISOString() }
    );
  }

  async withOffices(params?: DivisionsFilter, withFloors = false) {
    const urlParams = this.formatGenericParams(params);
    urlParams.set('return_type', 'tree');
    if (withFloors) {
      urlParams.set('return_floors', 'true');
    }
    return this.requestWithStatus<DivisionWithOffices>(
      'GET',
      `${BASE_PATH}?${urlParams.toString()}`
    );
  }
}
